import { Typography } from '@mui/material';
import { format } from 'date-fns';
import {
  Fact,
  LedgerHoverCard,
  RefCode,
  StatusWord,
  formatLedgerWhenFull,
  percentOf,
  workStatusTokens,
  type WorkStatus,
} from '../../../shared/components/ledger';
import { formatCurrency } from '../../../shared/utils/currency';

/**
 * Harness composition of the booked-work fact list. Facts are all conditional
 * — which of them a real screen can render depends on what its DTO carries,
 * so the shared layer ships the card and the Fact row, not the fact list.
 */

export interface WorkHoverFacts {
  title: string;
  kind: string;
  description?: string;
  status?: WorkStatus;
  reference?: string;
  sitePlot?: string;
  who?: string;
  whenIso?: string;
  amount?: number;
  target?: number;
  approvedBy?: string;
  approvedAt?: string;
  rejectedBy?: string;
  rejectionReason?: string;
  onOpenDetails?: () => void;
}

export function WorkHoverCard(facts: WorkHoverFacts) {
  const percent =
    facts.amount != null && facts.target != null
      ? percentOf(facts.amount, facts.target)
      : null;

  return (
    <LedgerHoverCard
      title={facts.title}
      kind={facts.kind}
      description={facts.description}
      onOpenDetails={facts.onOpenDetails ?? (() => {})}
    >
      {facts.status && (
        <Fact label="Status">
          <StatusWord token={workStatusTokens[facts.status]} />
        </Fact>
      )}
      {facts.approvedBy && (
        <Fact label="Approved by">
          {facts.approvedBy}
          {facts.approvedAt
            ? ` · ${format(new Date(facts.approvedAt), 'MMM d')}`
            : ''}
        </Fact>
      )}
      {facts.rejectedBy && <Fact label="Rejected by">{facts.rejectedBy}</Fact>}
      {facts.rejectionReason && (
        <Typography
          variant="caption"
          color="text.secondary"
          sx={{ display: 'block', py: 0.25 }}
        >
          “{facts.rejectionReason}”
        </Typography>
      )}
      {facts.reference && (
        <Fact label="Ref">
          <RefCode>{facts.reference}</RefCode>
        </Fact>
      )}
      {facts.sitePlot && <Fact label="Site · Plot">{facts.sitePlot}</Fact>}
      {facts.who && <Fact label="Booked by">{facts.who}</Fact>}
      {facts.whenIso && (
        <Fact label="When">{formatLedgerWhenFull(facts.whenIso)}</Fact>
      )}
      {facts.amount != null && (
        <Fact label="Value">{formatCurrency(facts.amount)}</Fact>
      )}
      {facts.target != null && (
        <Fact label="Target">{formatCurrency(facts.target)}</Fact>
      )}
      {percent != null && percent < 100 && (
        <Fact label="Booked">{percent}%</Fact>
      )}
    </LedgerHoverCard>
  );
}
